import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';

/**
 * Org platform-subscription entitlement lockout interceptor (KAN-356 Phase 8
 * task 8.4c, epic KAN-331). The FE half of the dark-launch entitlement gate
 * whose backend DECISION + ENFORCEMENT ship in task 8.3a/8.3b.
 *
 * When a billing_required org is not entitled, the central dispatcher
 * (handlers/api.ts) returns a 402 PAYMENT_REQUIRED on gated content paths with
 * code ORG_SUBSCRIPTION_REQUIRED / ORG_BILLING_PAST_DUE and error details
 * { reason, organizationId }. This interceptor routes ONLY those responses to
 * the chrome-less /billing/locked page and rethrows the error so any
 * component-level handling still runs.
 *
 * DARK-LAUNCH INVARIANT: every existing org is billing_required=false, so the
 * gate decides `exempt` server-side and this 402 never fires. Every other
 * response - success or any non-lockout error - passes through untouched, so
 * the interceptor is a provable no-op until an org opts in. (Pinned by the
 * pass-through specs.)
 */
const LOCKOUT_CODES = ['ORG_SUBSCRIPTION_REQUIRED', 'ORG_BILLING_PAST_DUE'];
const LOCKOUT_ROUTE = '/billing/locked';

/**
 * Recovery surfaces (KAN-356 task 8.7): routes a locked-org admin can
 * legitimately reach WHILE resolving the lockout - the lockout page itself, the
 * org-settings billing tab (where the Subscribe / Manage-billing panel lives),
 * and the user profile. These map 1:1 onto the backend gate's EXEMPT API
 * allowlist (/payments, /organizations, /users), so the recovery reads
 * themselves return 200. But the app-shell (MainLayout) fires always-on
 * BACKGROUND polls on every chrome'd page (GET /notifications,
 * /notifications/unread-count, /invitations, /certificates) that ARE gated and
 * 402 for a locked org. Redirecting to /billing/locked on those background
 * 402s bounces the admin off the recovery surface BEFORE the Subscribe panel is
 * reachable = a dead end (the "Go to billing" CTA loops back to lockout),
 * defeating the "a lockout is never a dead end" intent. So while the user is
 * already on a recovery surface, suppress the redirect: any 402 there is a
 * background poll, never a primary content navigation. A real content
 * navigation still fires its 402 from a NON-recovery route and locks out
 * normally. Extends the original already-on-/billing/locked loop guard.
 */
const RECOVERY_PATH_MATCHERS: RegExp[] = [
  /^\/billing\/locked(\/|$)/,
  /^\/admin\/organizations\/[^/]+\/settings(\/|$)/,
  /^\/profile(\/|$)/
];

@Injectable()
export class EntitlementLockoutInterceptor implements HttpInterceptor {
  constructor(private router: Router) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        this.maybeRouteToLockout(error);
        return throwError(() => error);
      })
    );
  }

  private maybeRouteToLockout(error: HttpErrorResponse): void {
    if (!(error instanceof HttpErrorResponse) || error.status !== 402) {
      return;
    }

    const body = error.error?.error;
    if (!body || !LOCKOUT_CODES.includes(body.code)) {
      return;
    }

    // Recovery-surface guard (task 8.7): while the admin is already on a
    // recovery surface (lockout page / org-settings billing tab / profile), a
    // lockout-code 402 is a background app-shell poll, not a primary content
    // navigation - never bounce them off the surface that lets them subscribe.
    if (this.isOnRecoverySurface()) {
      return;
    }

    const details = body.details || {};
    this.router.navigate([LOCKOUT_ROUTE], {
      queryParams: {
        organizationId: details.organizationId ?? null,
        reason: details.reason ?? null
      }
    });
  }

  private isOnRecoverySurface(): boolean {
    const path = this.currentPath();
    return RECOVERY_PATH_MATCHERS.some((matcher) => matcher.test(path));
  }

  private currentPath(): string {
    return (this.router.url || '').split('?')[0];
  }
}
